package com.arena_web.ui.sections.chat.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatDto
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.data.dto.ChatMemberDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

private enum class DetailsTab { Members, Media, Files, Links }

// Figma 8418:118878 (direct) / 8548:188126 (community) — chat details screen.
@Composable
fun MobileChatDetails(
    chat: ChatDto?,
    fallbackListItem: ChatListItemDto?,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    notificationsOn: Boolean,
    onToggleNotifications: () -> Unit,
    onBack: () -> Unit,
    onDeleteChat: () -> Unit = {},
    onLeaveChat: () -> Unit = {},
    // Group-only — the screen renders the affordances but the backend is the
    // source of truth on authorization (OWNER/ADMIN); we mirror that gate here.
    onRemoveMember: (userId: String) -> Unit = {},
    onInviteMembers: () -> Unit = {},
    onEditGroup: () -> Unit = {},
) {
    val t = LocalAppStrings.current
    val type = (chat?.type ?: fallbackListItem?.type ?: "DIRECT").uppercase()
    val partnerMember = if (type == "DIRECT") {
        chat?.members?.firstOrNull { it.userId != currentUserId }
    } else null
    val partnerUserId = when {
        type != "DIRECT" -> null
        partnerMember != null -> partnerMember.userId
        else -> fallbackListItem?.otherUserId
    }
    val name = chat?.name
        ?: fallbackListItem?.name
        ?: partnerMember?.fullName?.takeIf { it.isNotBlank() }
        ?: partnerMember?.username
        ?: t.chatDefaultName
    val imageUrl = chat?.imageUrl ?: fallbackListItem?.imageUrl ?: partnerMember?.avatarUrl
    val cardImageUrl = fallbackListItem?.cardImageUrl ?: partnerMember?.cardImageUrl
    val cardAvatarUrl = fallbackListItem?.cardAvatarUrl ?: partnerMember?.cardAvatarUrl
    val partnerOnline = partnerUserId?.let { it in onlineUserIds } == true
    val statusLabel = when {
        type != "DIRECT" -> chat?.members?.size?.let { t.chatMembersCount(it) } ?: ""
        partnerOnline -> t.chatOnline
        else -> t.chatOffline
    }
    val statusColor = if (partnerOnline) Color("#00E5A4") else Color("var(--arena-text-muted)")

    val isGroup = type == "GROUP"
    val members = chat?.members.orEmpty()
    // Admin = the chat owner or an ADMIN-role member. Used purely to decide
    // which affordances to show; the backend independently enforces the same
    // rule, so a non-admin who forces a call still gets rejected server-side.
    val isOwner = chat?.creatorId != null && chat.creatorId == currentUserId
    val myRole = members.firstOrNull { it.userId == currentUserId }?.role?.uppercase()
    val isAdmin = isOwner || myRole == "OWNER" || myRole == "ADMIN"

    // Groups open on the Members tab; direct chats have no Members tab so they
    // start on Media. Keyed on isGroup so the default re-resolves if the type
    // arrives after the first compose.
    var tab by remember(isGroup) {
        mutableStateOf(if (isGroup) DetailsTab.Members else DetailsTab.Media)
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(top = 15.px)
            .gap(18.px)
            .styleModifier {
                // Whole screen scrolls — profile block, tabs, and tab content
                // all move together so the user can reach everything even on
                // shorter viewports / when the keyboard is up. The bottom
                // padding pushes the destructive action above the home
                // indicator / nav-safe area when the content does overflow.
                property("overflow-y", "auto")
                property("overflow-x", "hidden")
                property("scrollbar-width", "none")
                property("min-height", "0")
                property("padding-bottom", "140px")
            },
    ) {
        // Profile block — back button anchored top-left (Box relative), the
        // player card sits centred at the same top edge so both align on
        // their first pixel. Name + status follow below.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 14.px)
                .styleModifier {
                    // Establish the positioning context for the back button
                    // so its `position: absolute` anchors to this Box rather
                    // than escaping up to the page.
                    property("position", "relative")
                }
        ) {
            // Back button — absolute, pinned to the top-left of the profile
            // block so the card below can occupy the centre without leaving
            // a 44 px vertical gap.
            Box(
                modifier = Modifier
                    .size(44.px)
                    .borderRadius(50.percent)
                    .cursor(Cursor.Pointer)
                    .onClick { onBack() }
                    .styleModifier {
                        property("position", "absolute")
                        property("top", "0")
                        property("left", "0")
                        property("z-index", "1")
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    src = AppIcons.ChevronForward,
                    modifier = Modifier.size(18.px).styleModifier {
                        property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                        property("transform", "rotate(180deg)")
                    }
                )
            }

        // Centred avatar + name + status column. Top of the card aligns with
        // the top of the back button because both share the y=0 baseline.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .gap(4.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().gap(12.px),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // Avatar (+ edit pencil for group admins). Relative wrapper so
                // the pencil pins to the avatar's top-right corner per Figma
                // 8548:188153.
                Box(modifier = Modifier.styleModifier { property("position", "relative") }) {
                    if (cardImageUrl != null) {
                        // Override `--scale` locally so the PlayerCard renders at
                        // its design size (XtraSmall = 58×90 → ≈87×135 here).
                        // This is cleaner than `transform: scale` because the
                        // bounding box matches the visible card, so the name
                        // below sits at the correct distance instead of being
                        // overlapped by inner artwork.
                        Box(modifier = Modifier.styleModifier { property("--scale", "1.5px") }) {
                            PlayerCard(
                                playerName = "",
                                rating = 0,
                                position = "",
                                sport = "football",
                                size = PlayerCardSize.XtraSmall,
                                cardAssetsBasePath = null,
                                cardTextColor = null,
                                cardFrameUrl = cardImageUrl,
                                avatarUrl = cardAvatarUrl ?: AppIcons.EmptyAvatar,
                                hideRating = true,
                                hideShadow = true,
                            )
                        }
                    } else {
                        LargeRoundAvatar(name = name, imageUrl = imageUrl)
                    }

                    if (isGroup && isAdmin) {
                        EditPencilButton(onClick = onEditGroup)
                    }
                }
                SpanText(
                    name,
                    modifier = Modifier
                        .fontSize(20.px)
                        .fontWeight(500)
                        .lineHeight(26.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
            SpanText(
                statusLabel,
                modifier = Modifier
                    .fontSize(16.px)
                    .color(statusColor)
            )
        }
        }

        // Gradient divider — same `BorderInnerL` token the desktop info
        // panel uses (Figma "Section Underline").
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .styleModifier {
                    // Slightly grey hairline that fades out at both edges —
                    // matches the "Section Underline" treatment used across
                    // the desktop chat panels.
                    property(
                        "background",
                        "linear-gradient(90deg, transparent 0%, rgba(190,214,245,0.25) 50%, transparent 100%)"
                    )
                }
        )

        // Notifications row.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 16.px),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(
                modifier = Modifier.gap(12.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    src = AppIcons.Notifications,
                    modifier = Modifier.size(22.px).styleModifier {
                        property("filter", com.arena_web.ui.theme.ThemeVars.ICON_FILTER_V)
                    }
                )
                SpanText(
                    t.chatNotificationsLabel,
                    modifier = Modifier
                        .fontSize(17.px)
                        .lineHeight(23.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
            IosSwitch(on = notificationsOn, onToggle = onToggleNotifications)
        }

        // Tabs row + underline strip — the blue segment paints OVER the grey
        // base line (not above it) so the two share the same vertical
        // position. Each tab owns an equal-width slot so its bottom underline
        // is exactly 1/3 of the strip; the grey base spans the full width
        // and the blue sits flush on top of the active slot.
        Column(
            modifier = Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Row(modifier = Modifier.fillMaxWidth().padding(leftRight = 20.px)) {
                if (isGroup) {
                    TabLabel(t.chatTabMembers, tab == DetailsTab.Members) { tab = DetailsTab.Members }
                }
                TabLabel(t.chatTabMedia, tab == DetailsTab.Media) { tab = DetailsTab.Media }
                TabLabel(t.chatTabFiles, tab == DetailsTab.Files) { tab = DetailsTab.Files }
                TabLabel(t.chatTabLinks, tab == DetailsTab.Links) { tab = DetailsTab.Links }
            }
        }

        // Tab content — flows with the rest of the page; the outer Column is
        // the scroll container so the whole screen moves together.
        Div(
            attrs = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("padding", "0 8px 24px 8px")
                }
                .toAttrs()
        ) {
            when (tab) {
                DetailsTab.Members -> MembersSection(
                    members = members,
                    onlineUserIds = onlineUserIds,
                    currentUserId = currentUserId,
                    isAdmin = isAdmin,
                    onInviteMembers = onInviteMembers,
                    onRemoveMember = onRemoveMember,
                )
                DetailsTab.Media -> EmptyTab(t.chatNoMediaSharedYet)
                DetailsTab.Files -> EmptyTab(t.chatNoFilesSharedYet)
                DetailsTab.Links -> EmptyTab(t.chatNoLinksSharedYet)
            }
        }

        // Destructive action — mirrors the desktop ChatInfoPanel:
        //  • Direct chat → "Delete chat" (red trash)
        //  • Group, you're the owner → "Delete group"
        //  • Group, you're a member → "Leave group" (door icon)
        val isGroup = type == "GROUP"
        val creatorId = chat?.creatorId
        val isOwner = creatorId != null && creatorId == currentUserId
        val (actionLabel, actionIcon, actionHandler) = when {
            !isGroup -> Triple(t.chatDeleteChatAction, AppIcons.Trash, onDeleteChat)
            isOwner -> Triple(t.chatDeleteGroupAction, AppIcons.Trash, onDeleteChat)
            else -> Triple(t.chatLeaveGroupAction, AppIcons.Enter, onLeaveChat)
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .styleModifier {
                    // Slightly grey hairline that fades out at both edges —
                    // matches the "Section Underline" treatment used across
                    // the desktop chat panels.
                    property(
                        "background",
                        "linear-gradient(90deg, transparent 0%, rgba(190,214,245,0.25) 50%, transparent 100%)"
                    )
                }
        )
        DestructiveAction(
            label = actionLabel,
            iconSrc = actionIcon,
            onClick = actionHandler,
        )
    }
}

// Edit-avatar pencil — pinned to the avatar's top-right (Figma 8548:188154).
// Group admins only; the caller gates rendering.
@Composable
private fun EditPencilButton(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(32.px)
            .borderRadius(50.percent)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "absolute")
                property("top", "-2px")
                property("right", "-2px")
                property("background", "#06122C")
                property("border", "0.3px solid rgba(255,255,255,0.4)")
            },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier.size(14.px).styleModifier {
                property("background-color", "#BED6F5")
                property("mask", "url('${AppIcons.Pencil}') no-repeat center / contain")
                property("-webkit-mask", "url('${AppIcons.Pencil}') no-repeat center / contain")
            }
        )
    }
}

// Members tab — collapsible "Members (N)" header, an invite row, and the
// member list. Remove (×) buttons appear only for admins (Figma 8548:188173).
@Composable
private fun MembersSection(
    members: List<ChatMemberDto>,
    onlineUserIds: Set<String>,
    currentUserId: String?,
    isAdmin: Boolean,
    onInviteMembers: () -> Unit,
    onRemoveMember: (String) -> Unit,
) {
    val t = LocalAppStrings.current
    var expanded by remember { mutableStateOf(true) }

    Column(modifier = Modifier.fillMaxWidth().gap(18.px)) {
        // Header — title + collapse chevron.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 16.px)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded },
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            SpanText(
                t.chatMembersHeader(members.size),
                modifier = Modifier
                    .fontSize(18.px)
                    .lineHeight(23.px)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Box(
                modifier = Modifier.size(16.px).styleModifier {
                    property("background-color", "#BED6F5")
                    property("mask", "url('${AppIcons.ChevronDown}') no-repeat center / contain")
                    property("-webkit-mask", "url('${AppIcons.ChevronDown}') no-repeat center / contain")
                    property("transform", if (expanded) "rotate(180deg)" else "rotate(0deg)")
                    property("transition", "transform 0.2s ease")
                }
            )
        }

        if (expanded) {
            // Invite members — any member can invite (backend allows it).
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 16.px)
                    .gap(18.px)
                    .cursor(Cursor.Pointer)
                    .onClick { onInviteMembers() },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .size(44.px)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("border", "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                    "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Box(
                        modifier = Modifier.size(20.px).styleModifier {
                            property("background-color", "#0061FF")
                            property("mask", "url('${AppIcons.Add}') no-repeat center / contain")
                            property("-webkit-mask", "url('${AppIcons.Add}') no-repeat center / contain")
                        }
                    )
                }
                SpanText(
                    t.chatInviteMembersTitle,
                    modifier = Modifier
                        .fontSize(18.px)
                        .lineHeight(23.px)
                        .color(Color("#0061FF"))
                )
            }

            // Member list.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(16.px)
                    .styleModifier { property("padding", "0 12px 0 16px") },
            ) {
                members.forEach { member ->
                    MemberRow(
                        member = member,
                        isOnline = member.userId in onlineUserIds,
                        // Admins can remove anyone but the owner and themselves.
                        canRemove = isAdmin &&
                            member.userId != currentUserId &&
                            member.role.uppercase() != "OWNER",
                        onRemove = { onRemoveMember(member.userId) },
                    )
                }
            }
        }
    }
}

@Composable
private fun MemberRow(
    member: ChatMemberDto,
    isOnline: Boolean,
    canRemove: Boolean,
    onRemove: () -> Unit,
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Row(
            modifier = Modifier.gap(8.px).styleModifier { property("min-width", "0") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MemberAvatar(member)
            Column(
                modifier = Modifier.gap(2.px).styleModifier { property("min-width", "0") },
            ) {
                SpanText(
                    member.fullName?.takeIf { it.isNotBlank() } ?: member.username,
                    modifier = Modifier
                        .fontSize(18.px)
                        .fontWeight(500)
                        .lineHeight(22.px)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier {
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                )
                SpanText(
                    if (isOnline) t.chatOnline else t.chatOffline,
                    modifier = Modifier
                        .fontSize(14.px)
                        .fontWeight(300)
                        .lineHeight(18.px)
                        .color(if (isOnline) Color("#00E5A4") else Color("var(--arena-text-muted)"))
                )
            }
        }

        if (canRemove) {
            RemoveMemberButton(onRemove = onRemove)
        }
    }
}

// Bare red × by default; the red-tinted circle fades in on hover (desktop) or
// while pressed (mobile). The 36 px box is always present so rows don't reflow.
@Composable
private fun RemoveMemberButton(onRemove: () -> Unit) {
    var active by remember { mutableStateOf(false) }
    Box(
        modifier = Modifier
            .size(44.px)
            .flexShrink(0)
            .cursor(Cursor.Pointer)
            .onClick { onRemove() }
            .onMouseEnter { active = true }
            .onMouseLeave { active = false }
            .onTouchStart { active = true }
            .onTouchEnd { active = false }
            .onTouchCancel { active = false },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier
                .size(36.px)
                .borderRadius(50.percent)
                .styleModifier {
                    property("transition", "background 0.15s ease, border 0.15s ease")
                    if (active) {
                        property("background", "rgba(255, 66, 66, 0.15)")
                        property("border", "0.8px solid #FF4242")
                    } else {
                        property("background", "transparent")
                        property("border", "0.8px solid transparent")
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier.size(14.px).styleModifier {
                    property("background-color", "#FF4242")
                    property("mask", "url('${AppIcons.Close}') no-repeat center / contain")
                    property("-webkit-mask", "url('${AppIcons.Close}') no-repeat center / contain")
                }
            )
        }
    }
}

// Member avatar — mini player card (29×44) when the member has one, else a
// round avatar with image or initials.
@Composable
private fun MemberAvatar(member: ChatMemberDto) {
    val cardFrameUrl = member.cardImageUrl
    if (cardFrameUrl != null) {
        Box(
            modifier = Modifier.flexShrink(0).styleModifier {
                property("width", "29px")
                property("height", "44px")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                // XtraSmall 58×90 design → 29×45 at this scale.
                property("--scale", "0.5px")
            },
            contentAlignment = Alignment.Center,
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = "football",
                size = PlayerCardSize.XtraSmall,
                cardAssetsBasePath = null,
                cardTextColor = null,
                cardFrameUrl = cardFrameUrl,
                avatarUrl = member.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                hideRating = true,
                hideShadow = true,
            )
        }
    } else {
        val imageUrl = member.avatarUrl
        val initials = (member.fullName ?: member.username).split(" ")
            .filter { it.isNotBlank() }
            .take(2)
            .joinToString("") { it.first().uppercase() }
            .ifBlank { "?" }
        Box(
            modifier = Modifier
                .size(40.px)
                .flexShrink(0)
                .borderRadius(50.percent)
                .overflow(Overflow.Hidden)
                .backgroundColor(Color("#1A2238")),
            contentAlignment = Alignment.Center,
        ) {
            if (imageUrl != null) {
                Div(attrs = {
                    ref { el ->
                        el.innerHTML = "<img src=\"$imageUrl\" referrerpolicy=\"no-referrer\" " +
                            "style=\"width:100%;height:100%;object-fit:cover;\" />"
                        onDispose {}
                    }
                    style { property("width", "100%"); property("height", "100%") }
                })
            } else {
                SpanText(
                    initials,
                    modifier = Modifier.fontSize(16.px).color(Color("#FAFAFB"))
                )
            }
        }
    }
}

@Composable
private fun DestructiveAction(label: String, iconSrc: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 20.px, topBottom = 14.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier.size(20.px).styleModifier {
                // Same hex as AppColors.Error so the icon and label read as
                // one continuous accent.
                property("background-color", "#FF1A1A")
                property("mask", "url('$iconSrc') no-repeat center / contain")
                property("-webkit-mask", "url('$iconSrc') no-repeat center / contain")
            }
        )
        SpanText(
            label,
            modifier = Modifier
                .fontSize(16.px)
                .padding(left = 12.px)
                .color(AppColors.Error)
        )
    }
}

@Composable
private fun TabLabel(label: String, active: Boolean, onClick: () -> Unit) {
    // Equal-width slot (`flex: 1`) so the bottom underline is always 1/3 of
    // the row. The label centres inside its slot. Active state paints the
    // 2 px underline blue ON the grey strip; inactive shows the same grey,
    // so the line stays continuous across all three tabs.
    Column(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .gap(8.px)
            .styleModifier { property("flex", "1") },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .fontSize(18.px)
                .lineHeight(23.px)
                .color(if (active) org.jetbrains.compose.web.css.Color(Color("#0061FF").toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                .styleModifier { property("white-space", "nowrap") }
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(1.px)
                .styleModifier {
                    // Active slot paints the project primary; inactive slots
                    // share the same `BorderInnerL` gradient as the section
                    // dividers so all three tabs visually form one continuous
                    // line and the blue sits ON it, not above it.
                    property(
                        "background",
                        if (active) "#0061FF" else AppGradients.BorderInnerL
                    )
                    property("transition", "background 0.2s ease")
                }
        )
    }
}

@Composable
private fun MediaGrid() {
    // Placeholder grid — once a media-history API ships these slots will be
    // populated. Tiles are fluid (`flex: 1 0 0` + `aspect-ratio: 1`) so they
    // share the row width equally and never overflow / clip regardless of
    // viewport size.
    Column(modifier = Modifier.fillMaxWidth().gap(8.px)) {
        repeat(2) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(8.px),
                horizontalArrangement = Arrangement.Center,
            ) {
                repeat(3) {
                    Box(
                        modifier = Modifier
                            .borderRadius(10.px)
                            .styleModifier {
                                property("flex", "1 0 0")
                                property("aspect-ratio", "1 / 1")
                                property("background", "#0E1A33")
                                property("border", "0.5px solid var(--l-hairline, rgba(255,255,255,0.08))")
                            }
                    )
                }
            }
        }
    }
}

@Composable
private fun EmptyTab(text: String) {
    Box(
        modifier = Modifier.fillMaxWidth().minHeight(120.px),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(text, modifier = Modifier.fontSize(14.px).color(com.arena_web.ui.theme.ThemeVars.Text3))
    }
}

// iOS-style toggle — green when on, grey rounded when off. Matches the
// Figma "Switch / Status=Active" component.
@Composable
private fun IosSwitch(on: Boolean, onToggle: () -> Unit) {
    Box(
        modifier = Modifier
            .width(51.px)
            .height(31.px)
            .borderRadius(31.px)
            .cursor(Cursor.Pointer)
            .onClick { onToggle() }
            .styleModifier {
                property("position", "relative")
                property("transition", "background 0.2s ease")
                property("background", if (on) "#0061FF" else "#3A3A3C")
            }
    ) {
        Box(
            modifier = Modifier
                .size(27.px)
                .borderRadius(50.percent)
                .backgroundColor(Color.white)
                .styleModifier {
                    property("position", "absolute")
                    property("top", "2px")
                    property("left", if (on) "22px" else "2px")
                    property("transition", "left 0.2s ease")
                    property("box-shadow", "0 2px 4px rgba(0,0,0,0.25)")
                }
        )
    }
}

@Composable
private fun LargeRoundAvatar(name: String, imageUrl: String?) {
    val initials = name.split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "?" }
    Box(
        modifier = Modifier
            .size(96.px)
            .borderRadius(50.percent)
            .overflow(Overflow.Hidden)
            .backgroundColor(Color("#1A2238")),
        contentAlignment = Alignment.Center,
    ) {
        if (imageUrl != null) {
            Div(attrs = {
                ref { el ->
                    el.innerHTML = "<img src=\"$imageUrl\" referrerpolicy=\"no-referrer\" " +
                        "style=\"width:100%;height:100%;object-fit:cover;\" />"
                    onDispose {}
                }
                style { property("width", "100%"); property("height", "100%") }
            })
        } else {
            SpanText(
                initials,
                modifier = Modifier
                    .fontSize(28.px)
                    .fontWeight(500)
                    .color(Color("#FAFAFB"))
            )
        }
    }
}
